import { motion } from 'framer-motion';
import styled, { css, keyframes } from 'styled-components';

import { styledLayout, styledMedia, styledTypography } from '@pkg/primitives';

import { HoverArrow, HoverUnderline } from '@app/pages/NFTDetailView/components/MainStage/components/NFTInfo/styles';

const loadingKeyFrames = keyframes`
    0% { opacity: 0.8 }
    50% { opacity: 1 }
    100% { opacity: 0.8 }
`;

export const CollectionThumbnail = styled(motion.div)<{ $image?: string }>`
    width: 40px;
    height: 40px;
    background: ${({ $image, theme }) => ($image ? `url(${$image})` : theme.shades.white[7])};
    background-repeat: no-repeat;
    background-size: cover;
    background-position: center;
    flex-shrink: 0;
    border-radius: 4px;
`;

export const MainContent = styled.div`
    margin-left: 20px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    height: 100%;
    padding: 5px 0;
`;

export const DetailsContent = styled.div`
    display: grid;
    grid-template-columns: auto auto auto;
    column-gap: 5px;
    row-gap: 15px;
    height: 100%;
`;

export const HeaderWrapper = styled.div`
    display: grid;
    grid-template-columns: auto 55px;
    gap: 8px;
`;

export const HeaderTextWrapper = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 0px;
    max-width: 200px;
    width: fit-content;
`;

export const Heading = styled.div<{ $isLoading?: boolean }>`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    position: relative;

    ${HoverUnderline} {
        top: unset;
        bottom: 0px;
    }

    &:hover {
        ${HoverUnderline} {
            transform: scaleX(1);
            transform-origin: 0 0;
        }
        ${HoverArrow} {
            transform: scale(0.7);
            transition-delay: 200ms;

            svg {
                opacity: 1;
                transform: translateX(0);
                transition-delay: 400ms;
            }
        }
    }

    height: 18px;
    width: min-content;
    max-width: 100%;

    font-weight: 700;
    font-size: 16px;
    line-height: 100%;
    letter-spacing: -0.02em;

    ${({ $isLoading }) =>
        $isLoading &&
        css`
            display: block;
            background: rgba(255, 255, 255, 0.05);
            width: 150px;
            border-radius: 5px;
            animation: ${loadingKeyFrames} 1s ease-in-out infinite;
            margin-bottom: 3px;
        `}
`;

export const SubHeading = styled.div<{ $isLoading?: boolean }>`
    height: 16px;
    font-weight: 700;
    font-size: 12px;
    line-height: 100%;
    display: flex;
    align-items: center;
    letter-spacing: -0.02em;
    color: rgba(255, 255, 255, 0.5);
    ${({ $isLoading }) =>
        $isLoading &&
        css`
            background: rgba(255, 255, 255, 0.05);
            width: 250px;
            border-radius: 5px;
            animation: ${loadingKeyFrames} 1s ease-in-out infinite;
        `}
`;

export const DetailsItem = styled.div<{ $isLoading?: boolean }>`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 11px 12px 12px 12px;
    gap: 4px;
    min-width: 85px;
    height: 63px;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 10px;

    ${({ $isLoading }) =>
        $isLoading &&
        css`
            animation: ${loadingKeyFrames} 1s ease-in-out infinite;
        `}
`;

export const DetailsTopLabel = styled.div`
    font-style: normal;
    font-weight: 600;
    font-size: 10px;
    line-height: 13px;
    color: rgba(255, 255, 255, 0.5);
`;

export const TimeLabel = styled.div`
    font-style: normal;
    font-weight: 600;
    font-size: 9px;
    line-height: 100%;
    color: rgba(255, 255, 255, 0.4);
`;

export const DetailsBottomLabel = styled.div`
    font-style: normal;
    font-weight: 600;
    font-size: 10px;
    line-height: 13px;
    color: rgba(255, 255, 255, 0.4);
    position: absolute;
    bottom: -8px;
    left: 0;
`;

export const DetailInner = styled.div`
    ${styledLayout.fullWidthFix};
    position: relative;
    display: flex;
    gap: 8px;
`;

export const TimeWrapper = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: flex-end;
    gap: 2px;
`;

export const Value = styled.div`
    ${styledLayout.fullWidthFix};
    display: flex;
    gap: 4px;
    align-items: center;
`;

export const ValuePrefix = styled.div`
    ${styledMedia.iconWrapper};
    padding: 2px;
    width: 16px;
    height: 16px;
    color: black;
    background-color: white;
    border-radius: 999px;
`;

export const ValueText = styled.p<{ $truncate?: boolean }>`
    ${styledTypography.allianceBase};
    ${styledTypography.textBoxTrim};
    flex: 1 1 auto;
    font-size: 20px;
    font-weight: 600;
    white-space: nowrap;

    ${({ $truncate }) => $truncate && styledTypography.truncate};
`;

export const BigImage = styled.div<{ $image?: string }>`
    width: 186px;
    height: 200px;
    border-radius: 10px;
    background-repeat: no-repeat;
    background-size: cover;
    background-position: center;
    background-color: ${({ theme }) => theme.shades.white[7]};
    ${({ $image }) =>
        $image &&
        css`
            background-image: url(${$image});
        `}
`;
